import { resolveOnboardingRole } from './roleUtils';

const createTabVisibility = (visibleTabIds) => {
  const visibleSet = visibleTabIds instanceof Set ? visibleTabIds : new Set(visibleTabIds || []);
  return (tabId) => visibleSet.has(tabId);
};

const filterAndSortSteps = (steps) => (
  steps
    .filter((step) => !step.hidden)
    .sort((left, right) => (left.priority || 0) - (right.priority || 0))
);

const pluralize = (count, singular, plural = `${singular}s`) => (
  `${count} ${count === 1 ? singular : plural}`
);

export const buildDashboardChecklist = ({
  actions,
  context = {},
  ordersEnabled,
  role,
  visibleTabIds
}) => {
  const roleKey = resolveOnboardingRole(role);
  const visible = createTabVisibility(visibleTabIds);
  const hasOrdersFeature = Boolean(ordersEnabled && (visible('order') || visible('orders') || visible('pipeline_board')));
  const canUseSettings = visible('settings');
  const canUseUsers = Boolean(canUseSettings && context.canAccessUsers);

  const descriptors = {
    owner: {
      title: 'Workspace Readiness',
      description: 'Finish the core setup so inventory, orders, workflow, and team access work together.'
    },
    admin: {
      title: 'Workspace Readiness',
      description: 'Finish the core setup so inventory, orders, workflow, and team access work together.'
    },
    manager: {
      title: 'Manager Priorities',
      description: 'Focus on the operational bottlenecks that should be handled first.'
    },
    operator: {
      title: 'Operator Start Here',
      description: 'These are the first workflow and inventory actions to complete in this workspace.'
    }
  };

  const adminLikeSteps = [
    {
      id: 'raw-materials',
      label: 'Add raw material types',
      detail: context.hasRawMaterials
        ? `${pluralize(context.rawMaterialCount || 0, 'raw material type')} configured.`
        : 'Define Level 1 raw materials and their characteristics in Settings.',
      done: Boolean(context.hasRawMaterials),
      hidden: !canUseSettings,
      priority: 10,
      actionLabel: 'Open Raw Materials',
      action: actions.openRawMaterials
    },
    {
      id: 'list-values',
      label: 'Add characteristic values',
      detail: context.hasRawMaterialValueOptions
        ? 'Dropdown value lists are already populated for your configured materials.'
        : 'Populate Manage Lists so teams can select real material characteristics in forms.',
      done: Boolean(context.hasRawMaterialValueOptions),
      hidden: !canUseSettings,
      priority: 20,
      actionLabel: 'Manage Lists',
      action: actions.openManageLists
    },
    {
      id: 'pipeline-stages',
      label: 'Set pipeline stages',
      detail: context.pipelineStageCount > 0
        ? `${pluralize(context.pipelineStageCount || 0, 'pipeline stage')} configured.`
        : 'Add workflow stages in Settings before orders start moving through production.',
      done: Number(context.pipelineStageCount || 0) > 0,
      hidden: !canUseSettings || !hasOrdersFeature,
      priority: 30,
      actionLabel: 'Open Settings',
      action: actions.openSystemSettings
    },
    {
      id: 'product-bom',
      label: 'Create one product with BOM',
      detail: context.hasProductWithBom
        ? 'At least one product already maps the raw materials required per unit.'
        : 'Add a product and define the raw materials required to build it.',
      done: Boolean(context.hasProductWithBom),
      hidden: !visible('product_add'),
      priority: 40,
      actionLabel: 'Add Product',
      action: actions.openProductAdd
    },
    {
      id: 'team-access',
      label: 'Invite or review team access',
      detail: context.userCount > 2
        ? `${pluralize((context.userCount || 0) - 2, 'additional user')} added beyond the default owner/admin accounts.`
        : 'Review User Access and add the people who should operate in this company.',
      done: Number(context.userCount || 0) > 2,
      hidden: !canUseUsers,
      priority: 50,
      actionLabel: 'User Access',
      action: actions.openUserAccess
    },
    {
      id: 'transaction',
      label: 'Record first stock movement',
      detail: context.hasTransactions
        ? `${pluralize(context.transactionCount || 0, 'movement')} already recorded.`
        : 'Create the first inbound or outbound movement so stock tracking starts with real data.',
      done: Boolean(context.hasTransactions),
      hidden: !visible('add'),
      priority: 60,
      actionLabel: 'Add Movement',
      action: actions.openAddMovement
    },
    {
      id: 'order',
      label: 'Create first order',
      detail: context.hasOrders
        ? `${pluralize(context.orderCount || 0, 'order')} already in the system.`
        : 'Create the first order so BOM usage, delivery flow, and workflow tracking are live.',
      done: Boolean(context.hasOrders),
      hidden: !ordersEnabled || !visible('order'),
      priority: 70,
      actionLabel: 'Create Order',
      action: actions.openCreateOrder
    },
    {
      id: 'workflow',
      label: 'Move one order through workflow',
      detail: context.hasWorkflowProgress
        ? 'At least one order has already advanced beyond the starting stage.'
        : 'Advance one order out of the first stage so workflow tracking becomes meaningful.',
      done: Boolean(context.hasWorkflowProgress),
      hidden: !ordersEnabled || !visible('pipeline_board'),
      priority: 80,
      actionLabel: 'Open Workflow',
      action: actions.openWorkflow
    }
  ];

  const managerSteps = [
    {
      id: 'workflow-overdue',
      label: 'Review overdue workflow',
      detail: Number(context.overdueOrderCount || 0) > 0
        ? `${pluralize(context.overdueOrderCount || 0, 'overdue order')} waiting in workflow.`
        : 'No overdue workflow items right now.',
      done: Number(context.overdueOrderCount || 0) === 0 && Number(context.activeOrderCount || 0) > 0,
      hidden: !ordersEnabled || !visible('pipeline_board') || Number(context.activeOrderCount || 0) === 0,
      priority: 10,
      actionLabel: 'Open Workflow',
      action: actions.openWorkflow
    },
    {
      id: 'stock-risk',
      label: 'Review low stock risk',
      detail: Number(context.lowStockCount || 0) > 0
        ? `${pluralize(context.lowStockCount || 0, 'stock group')} at low or critical level.`
        : context.stockGroupCount > 0
          ? 'No low-stock groups need attention right now.'
          : 'No stock groups are visible yet. Record movements to start stock tracking.',
      done: Number(context.lowStockCount || 0) === 0 && Number(context.stockGroupCount || 0) > 0,
      hidden: !visible('stock'),
      priority: 20,
      actionLabel: 'View Stock',
      action: actions.openStock
    },
    {
      id: 'active-orders',
      label: 'Review active order queue',
      detail: Number(context.activeOrderCount || 0) > 0
        ? `${pluralize(context.activeOrderCount || 0, 'active order')} currently in progress.`
        : context.hasOrders
          ? 'No active orders are in progress right now.'
          : 'Orders will appear here once the company starts creating them.',
      done: Number(context.activeOrderCount || 0) > 0,
      hidden: !ordersEnabled || !visible('orders'),
      priority: 30,
      actionLabel: 'View Orders',
      action: actions.openOrders
    },
    {
      id: 'transaction',
      label: 'Record first stock movement',
      detail: context.hasTransactions
        ? `${pluralize(context.transactionCount || 0, 'movement')} already recorded.`
        : 'Capture the first movement so inventory and workflow data stay connected.',
      done: Boolean(context.hasTransactions),
      hidden: !visible('add'),
      priority: 40,
      actionLabel: 'Add Movement',
      action: actions.openAddMovement
    }
  ];

  const operatorSteps = [
    {
      id: 'transaction',
      label: 'Record first stock movement',
      detail: context.hasTransactions
        ? `${pluralize(context.transactionCount || 0, 'movement')} already recorded.`
        : 'Record today’s first inbound or outbound movement to start your ledger.',
      done: Boolean(context.hasTransactions),
      hidden: !visible('add'),
      priority: 10,
      actionLabel: 'Add Movement',
      action: actions.openAddMovement
    },
    {
      id: 'stock-visible',
      label: 'Verify stock visibility',
      detail: Number(context.stockGroupCount || 0) > 0
        ? `${pluralize(context.stockGroupCount || 0, 'stock group')} visible in stock overview.`
        : 'Once movements are recorded, stock groups will appear in the overview.',
      done: Number(context.stockGroupCount || 0) > 0,
      hidden: !visible('stock'),
      priority: 20,
      actionLabel: 'Open Stock',
      action: actions.openStock
    },
    {
      id: 'ledger',
      label: 'Check movement history',
      detail: context.hasTransactions
        ? 'Review movement history to verify today’s entries and corrections.'
        : 'Movement history becomes useful after your first stock entry is recorded.',
      done: Boolean(context.hasTransactions),
      hidden: !visible('transactions'),
      priority: 30,
      actionLabel: 'Open Ledger',
      action: actions.openTransactions
    },
    {
      id: 'workflow',
      label: 'Advance one workflow item',
      detail: context.hasWorkflowProgress
        ? 'At least one order has already moved beyond the starting stage.'
        : Number(context.activeOrderCount || 0) > 0
          ? 'Move an order to its next stage when work is complete.'
          : 'Workflow items will appear here once orders are active.',
      done: Boolean(context.hasWorkflowProgress),
      hidden: !ordersEnabled || !visible('pipeline_board'),
      priority: 40,
      actionLabel: 'Open Workflow',
      action: actions.openWorkflow
    }
  ];

  const roleSteps = roleKey === 'owner' || roleKey === 'admin'
    ? adminLikeSteps
    : roleKey === 'manager'
      ? managerSteps
      : operatorSteps;

  const steps = filterAndSortSteps(roleSteps);
  const pendingSteps = steps.filter((step) => !step.done);
  const completedCount = steps.filter((step) => step.done).length;
  const descriptor = descriptors[roleKey] || descriptors.operator;

  return {
    roleKey,
    title: descriptor.title,
    description: descriptor.description,
    steps,
    pendingSteps,
    completedCount,
    totalCount: steps.length
  };
};
